import React, { useCallback, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import DragItemTypes from "../DragItemTypes";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  getContentParentsForAnthology,
  groupChildrenByParent,
} from "../organizeUtil";
import { equals, map, mergeLeft, pluck } from "ramda";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import {
  useAddToCollection,
  useRemoveContentFromCollection,
} from "../../collections/collectionServices";
import { useParentDrop } from "../use-parentDrop";
import { useDrag } from "react-dnd";
import { notBlank } from "../../../util/ext";
import { useDnD, useSelected } from "../OrganizeProvider";

import SidebarTreeItemLabel from "./SidebarTreeItemLabel";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { getIds } from "../../../util/mediaUtil";

const useStyles = makeStyles(theme => ({
  content: {
    backgroundColor: "transparent",
  },

  contentHighlighted: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },

  header: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.6rem",
  },

  iconContainer: {
    width: 0,
  },

  label: {
    color: "inherit",
  },

  labelHighlighted: {
    color: "white",
  },

  treeItem: {
    fontWeight: "inherit",
  },
}));

const SidebarCollectionTreeItem = props => {
  const classes = useStyles();
  const {
    child,
    count,
    nestedTree,
    icon,
    onClick,
    onLabelClick,
    label,
    parent,
    parentChildrenList,
    nodeId,
    nodeType,
    tooltipLabel,
    selected,
    showAlert,
    handleLabelClick,
    ...otherProps
  } = props;

  const collection = child;
  const dnd = useDnD();
  const { selectedContent, removeAllSelectedContent } = useSelected();

  const getTypographyProps = type => {
    switch (type) {
      case "root":
        return { classes: { root: classes.header } };
      default:
        return { classes: { root: classes.treeItem } };
    }
  };

  //-- Handle Drag and Drop Actions
  const addToCollection = useAddToCollection();
  const deleteModuleItems = useDeleteModuleItems();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              if (equals(prevParent.mediaType, "collection")) {
                await removeContentFromCollection.mutateAsync({
                  collectionId: prevParent.id,
                  contentIds: newChildren.ids,
                });
              }

              if (equals(prevParent.mediaType, "anthology")) {
                // Remove content from all collections in anthology
                const collections = getContentParentsForAnthology(
                  selectedContent,
                  prevParent,
                );

                await Promise.all(
                  map(collectionId =>
                    removeContentFromCollection.mutateAsync({
                      collectionId,
                      contentIds: getIds(selectedContent),
                    }),
                  ),
                  getIds(collections),
                );
                removeAllSelectedContent();
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync({
                  id: prevParent.id,
                  ids: newChildren.ids,
                });
              }
            }
          }
        }

        await addToCollection.mutateAsync({
          resourceIds: { id: collection.id },
          data: { ids: pluck("id", items) },
        });

        showAlert("success", ["Content Successfully Added"]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      addToCollection,
      collection,
      deleteModuleItems,
      selectedContent,
      showAlert,
      removeAllSelectedContent,
      removeContentFromCollection,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: mergeLeft(
      { parent, prevMediaList: parentChildrenList },
      collection,
    ),
    onDrop,
    accept: [DragItemTypes.CONTENT],
  });

  // Bug prevented drag when values passed to dep array
  // https://github.com/react-dnd/react-dnd/issues/3479
  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.COLLECTION,
      item: () => {
        dnd.updateDragAction("Move");
        return {
          id: collection.id,
          mediaType: "collection",
          parent,
          prevMediaList: parentChildrenList,
        };
      },
      end: () => dnd.updateDragAction(""),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [parentChildrenList],
  );

  // Create Draggable & Droppable Element
  // https://github.com/react-dnd/react-dnd/issues/1451
  const dragDropRef = useCallback(
    node => {
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <TreeItem
      ref={dragDropRef}
      itemId={nodeId}
      onClick={onLabelClick}
      classes={{
        iconContainer: classes.iconContainer,
        content:
          (isOver && canDrop) || selected
            ? classes.contentHighlighted
            : classes.content,
        label:
          (isOver && canDrop) || selected
            ? classes.labelHighlighted
            : classes.label,
      }}
      label={
        <SidebarTreeItemLabel
          item={{ parent, prevMediaList: parentChildrenList, child }}
          tooltipLabel={tooltipLabel}
          labelText={label}
          TypographyProps={getTypographyProps(nodeType)}
        />
      }
      {...otherProps}
    >
      {nestedTree}
    </TreeItem>
  );
};

export default SidebarCollectionTreeItem;
